/**
 * D-EZ-88 — what a race does to a CAREER, as the race reports it.
 *
 * The SUPER RARE RESULTS chart prints three rows that outlive the afternoon:
 *
 *   13  "Horrific wreck, driver dies.†"
 *   33  "Driver wrecks and is injured, out for rest of season.†"
 *   53  "Driver wrecks and is injured, out for [dice] races.†"
 *   41/61 "BLACK FLAG + immediate DQ for driver in TOP/MIDDLE group, and
 *          suspended [dice] races."
 *
 * The race engine resolved the in-race half (the driver is out / disqualified)
 * and left the rest as an untyped `pendingEffect` that nothing read. This is
 * the typed hand-off: the race decides WHO, WHAT and HOW LONG — every die is
 * rolled inside the race, through the engine's own `Dice`, so a hand-rolled
 * race asks for it and a seeded replay reproduces it — and the season applies
 * it (`seasonPlay.ts`, the "SUPER RARE" block next to the EZRA injuries).
 *
 * `[dice]` — RULING D-RW-1 (raceWeek.ts `DICE_RACES`): the corpus prints the
 * token eleven times and defines it nowhere; the game reads it as ONE d6
 * everywhere, so a Super Rare suspension is read the same way a Race Week one
 * is.
 */

export type CareerConsequenceKind = 'injury' | 'suspension' | 'fatality';

export interface CareerConsequence {
  driverId: string;
  kind: CareerConsequenceKind;
  /**
   * Races he misses AFTER this one. `'season'` = the rest of the season (an
   * injury on SUPER RARE 33, and a fatality, which the season handles as a
   * death rather than an absence).
   */
  races: number | 'season';
  /** The printed row, verbatim. */
  cause: string;
  ruleCitation: string;
  /** The race turn it happened on. */
  turn: number;
  /** The d6 that set the length, when the row printed `[dice]` (D-RW-1). */
  die?: number;
}

/** D-RW-1 — `[dice]` is one six-sided die. Mirrors raceWeek.ts `DICE_RACES`. */
export const CONSEQUENCE_DICE_SIDES = 6;

export const CITE_SUPER_RARE_CAREER =
  'RARE RESULTS sheet, SUPER RARE RESULTS rows 13/33/41/53/61 + D-RW-1 ([dice] = 1d6) — D-EZ-88';

/** One line a ledger or a booth can say, built only from the fields. */
export function describeConsequence(c: CareerConsequence, name: string): string {
  switch (c.kind) {
    case 'fatality':
      return `${name} lost his life in the wreck.`;
    case 'injury':
      return c.races === 'season'
        ? `${name} is hurt and out for the rest of the season.`
        : `${name} is hurt and will miss the next ${c.races} race${c.races === 1 ? '' : 's'}.`;
    case 'suspension':
      return c.races === 'season'
        ? `${name} is suspended for the rest of the season.`
        : `${name} is suspended for the next ${c.races} race${c.races === 1 ? '' : 's'}.`;
  }
}
